/**
 * TschaukoPortrait — animated avatar in the voice-overlay header.
 *
 * Plays the curated HLS portrait loop (storage object 36468 on arkserver,
 * a 9-second 1280×720 Dr.-Tschauko video). Uses hls.js where the browser
 * doesn't natively support HLS playback (everything except Safari).
 *
 * Behavior:
 *   - Muted, loops forever while the bot is speaking — visually mimics
 *     lip-sync without actually being synced. Pauses when the bot is
 *     idle/listening so the portrait reads as "at rest".
 *   - Falls back to a generic person SVG if HLS fails to load (host down,
 *     CORS issue, decoder error). Never throws.
 *   - Same `tsch-portrait--<status>` class on the wrapper so existing CSS
 *     ring/pulse states keep working.
 *
 * Self-contained — does not couple to AgentSessionContext; consumer passes
 * the current status as a prop.
 */

import { useEffect, useRef, useState } from 'react'
import type { SessionStatus } from '../lib/voice-agent/VoiceAgentController'

/** HLS master playlist for the Tschauko portrait clip. Lives on the
 *  arkserver storage instance (NOT the public api-storage.arkturian.com,
 *  which 404s for this id). */
const PORTRAIT_HLS_URL =
  'https://api-storage.arkserver.arkturian.com/uploads/storage/media/arkturian/u2_20260629_155336_6a6a6e88_transcoded/master.m3u8'

/** Fallback poster (still frame from the same media) shown until the
 *  video element produces its first frame, and as a graceful degrade
 *  target if HLS fails.
 *
 *  `variant=medium` (1280×720 = the source video's native size), NOT
 *  `thumbnail` — that is only 320×180 and was being upscaled across the whole
 *  screen, so the guide opened on a visibly blurry Dr. Tschauko (Issue #383).
 *  The cost of the sharp frame is 85 KB instead of 10 KB. */
const PORTRAIT_POSTER_URL =
  'https://api-storage.arkserver.arkturian.com/storage/media/36468?variant=medium&format=jpg'

interface Props {
  status: SessionStatus
}

export default function TschaukoPortrait({ status }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [failed, setFailed] = useState(false)

  // HLS attach — runs once on mount (until `failed` flips). The play /
  // pause toggle in response to `status` lives in a separate effect so
  // we don't tear down hls.js every time the bot changes state.
  useEffect(() => {
    const video = videoRef.current
    if (!video || failed) return

    let hlsInstance: { destroy: () => void } | null = null
    let cancelled = false

    async function setup() {
      if (!video) return
      // Safari + iOS have native HLS support — let the browser handle it.
      if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = PORTRAIT_HLS_URL
        return
      }
      // Other browsers — dynamic-import hls.js so we don't pay for it
      // unless the portrait actually mounts.
      try {
        const { default: Hls } = await import('hls.js')
        if (cancelled || !Hls.isSupported()) {
          if (!cancelled) setFailed(true)
          return
        }
        const hls = new Hls({ enableWorker: true, lowLatencyMode: false })
        hls.loadSource(PORTRAIT_HLS_URL)
        hls.attachMedia(video)
        hls.on(Hls.Events.ERROR, (_evt, data) => {
          if (data.fatal) {
            console.warn('[TschaukoPortrait] HLS fatal:', data.type, data.details)
            setFailed(true)
          }
        })
        hlsInstance = hls
      } catch (e) {
        console.warn('[TschaukoPortrait] hls.js load failed:', e)
        setFailed(true)
      }
    }
    void setup()

    return () => {
      cancelled = true
      if (hlsInstance) {
        try { hlsInstance.destroy() } catch { /* noop */ }
      }
    }
  }, [failed])

  // Belt-and-braces: ensure mute is set BEFORE any play attempt. The
  // attribute is enough in spec but some Chromium builds need the
  // property set for autoplay-policy to count us as "muted media".
  useEffect(() => {
    const video = videoRef.current
    if (video) {
      video.muted = true
      video.volume = 0
    }
  }, [failed])

  // Play while bot is speaking, pause otherwise. We always attempt play
  // immediately on a status flip; if the browser rejects (HLS still
  // buffering, autoplay policy), we install a one-shot `canplay` retry.
  // Pre-loading the source at mount means by the time the first
  // greeting fires we're usually already past `canplay`.
  useEffect(() => {
    const video = videoRef.current
    if (!video || failed) return
    const log = (window as unknown as {
      __voiceAgentLogInfo?: (e: string, ctx?: Record<string, unknown>) => void
    }).__voiceAgentLogInfo

    if (status === 'speaking') {
      let retryCleanup: (() => void) | null = null
      const tryPlay = (attempt: number) => {
        video.muted = true // re-assert just before play; some browsers
                           // un-mute on src change
        video.play()
          .then(() => {
            console.debug('[TschaukoPortrait] play ok attempt=', attempt, 'time=', video.currentTime)
            log?.('portrait_play_ok', { attempt, readyState: video.readyState })
          })
          .catch((e) => {
            const msg = e?.message || String(e)
            console.warn('[TschaukoPortrait] play rejected attempt=', attempt, msg)
            log?.('portrait_play_rejected', { attempt, msg, readyState: video.readyState })
            // Retry once on canplay if we haven't already.
            if (attempt === 0) {
              const onReady = () => {
                video.removeEventListener('canplay', onReady)
                if (status === 'speaking') tryPlay(1)
              }
              video.addEventListener('canplay', onReady, { once: true })
              retryCleanup = () => video.removeEventListener('canplay', onReady)
            }
          })
      }
      tryPlay(0)
      return () => { if (retryCleanup) retryCleanup() }
    } else {
      video.pause()
      log?.('portrait_pause', { status })
      if (status === 'idle' || status === 'closed' || status === 'error') {
        try { video.currentTime = 0 } catch { /* noop */ }
      }
    }
  }, [status, failed])

  return (
    <div
      className={`tsch-overlay__portrait tsch-portrait--${status}`}
      aria-label="Dr. Peter Tschauko"
    >
      {failed ? (
        // Graceful degrade: same generic SVG silhouette as the original
        // placeholder. Keeps the ring/pulse animation intact.
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="8" r="4" />
          <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" />
        </svg>
      ) : (
        <video
          ref={videoRef}
          className="tsch-overlay__portrait-video"
          poster={PORTRAIT_POSTER_URL}
          muted
          loop
          playsInline
          preload="auto"
          aria-hidden="true"
        />
      )}
    </div>
  )
}
